import dayjs from "dayjs";
import { supabase } from "../../config/supabaseClient";
import utc from "dayjs/plugin/utc"; // Import the UTC plugin

dayjs.extend(utc);

/**
 * Accepted image MIME types for all upload inputs — HEIC/HEIF are intentionally excluded
 * because they are not universally supported by browsers or CDNs.
 */
export const ACCEPTED_IMAGE_TYPES =
  "image/jpeg,image/jpg,image/png,image/gif,image/webp,image/bmp,image/svg+xml,image/avif";

/** Storage bucket used by Email Campaigns. Website campaign uploads must use this same bucket. */
export const CAMPAIGN_IMAGE_BUCKET = "campaign-image";

/** Returns true when the file is HEIC or HEIF format, which we do not allow. */
export function isHeicFile(file: File): boolean {
  const mime = (file.type || "").toLowerCase();
  if (mime === "image/heic" || mime === "image/heif" ||
      mime === "image/heic-sequence" || mime === "image/heif-sequence") {
    return true;
  }
  // Also catch files where the browser reports no MIME type but the extension is heic/heif
  const name = (file.name || "").toLowerCase();
  return name.endsWith(".heic") || name.endsWith(".heif");
}

type HandleFileUploadProps = {
  info: any;
  username: string;
  productName?: string;
  setFileList: (fileList: any[]) => void;
  setProductImage: (url: string) => void;
  storageName: string;
};

/** Ant Design Upload may pass `RcFile` with `originFileObj` or a native `File`. */
function resolveUploadBlob(file: any): File {
  if (file instanceof File) return file;
  const o = file?.originFileObj;
  if (o instanceof File) return o;
  throw new Error("No file payload for upload");
}

function extensionForUpload(file: File, displayName: string): {
  mediaType: string;
  mediaExt: string;
} {
  const mime = (file.type || "").toLowerCase();
  if (mime.includes("/")) {
    const [t, sub] = mime.split("/");
    const ext = sub === "jpeg" ? "jpg" : sub;
    return { mediaType: t || "image", mediaExt: ext || "jpg" };
  }
  const fromName = displayName.includes(".")
    ? displayName.replace(/^.*\./, "").toLowerCase()
    : "";
  const safe =
    fromName && /^[a-z0-9]{2,5}$/.test(fromName) ? fromName : "jpg";
  return { mediaType: "image", mediaExt: safe };
}

export async function uploadImageFile({
  file,
  username,
  storageName,
}: {
  file: File;
  username: string;
  storageName: string;
}): Promise<string> {
  const rawFile = resolveUploadBlob(file);

  if (isHeicFile(rawFile)) {
    throw new Error(
      "HEIC/HEIF images are not supported. Please convert to JPEG, PNG, or WebP before uploading."
    );
  }

  const displayName = rawFile.name || "upload";
  const { mediaType, mediaExt } = extensionForUpload(rawFile, displayName);
  const sanitizedFileName = displayName
    .replace(/['",;:/?|\_=\-\\]/g, "")
    .replace(/\s+/g, "-")
    .replace(/[^a-zA-Z0-9\-]/g, "");
  const timestamp = Date.now();
  const post_id = `${sanitizedFileName || "image"}-${timestamp}`;
  const parsedDate = dayjs();
  const formattedDate = parsedDate
    .utcOffset(330)
    .format("ddd MMM DD YYYY HH:mm:ss [GMT+0530]");
  const pacificDateTime = new Date(formattedDate);
  const post_date =
    pacificDateTime?.toISOString()?.slice(0, 19).replace("T", " ") + "-00:00";
  const datePart = post_date.split(" ")[0];
  const objectPath = `${username}/${datePart}/${mediaType}${post_id}.${mediaExt}`;

  const { data, error } = await supabase.storage
    .from(`${storageName}`)
    .upload(objectPath, rawFile, {
      // Paths include a timestamp and are not overwritten (upsert: false).
      cacheControl: "31536000",
      upsert: false,
    });

  if (error) {
    throw error;
  }

  const storedPath = data?.path ?? objectPath;
  const { data: publicUrlData } = supabase.storage
    .from(`${storageName}`)
    .getPublicUrl(storedPath);

  const url = publicUrlData?.publicUrl || "";
  if (!url) {
    throw new Error("Could not resolve public URL for upload");
  }

  return url;
}

export const handleProductFileUpload = async ({
  info,
  username,
  productName,
  setFileList,
  setProductImage,
  storageName,
}: HandleFileUploadProps) => {
  const { file } = info;
  try {
    file.status = "uploading";
    const fileListCopy = [...info.fileList].slice(-1);
    setFileList(fileListCopy);

    const url = await uploadImageFile({
      file,
      username,
      storageName,
    });

    file.status = "done";
    setProductImage(url);
    return { publicUrl: url };
  } catch (error) {
    console.error("Upload Error:", error);
    throw error;
  }
};
